/**
 * A random ID for this browser, sent with check-ins, check-outs, logins and
 * logouts so super admins can tell one computer from another (the API keeps it
 * hidden: utils/deviceId.js there). Employees see nothing, and nothing depends
 * on it: if storage is blocked, the request simply goes without it.
 *
 * Kept in localStorage AND a long-lived cookie, because logging out clears
 * localStorage (localStorage.clear() in the logout modal and the user menu).
 */
import Cookies from "js-cookie";

const KEY = "saasaki_device";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const FIVE_YEARS_IN_DAYS = 5 * 365;

function newId(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  // Pages served over plain http have no randomUUID; build a v4 UUID by hand.
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** This browser's ID (created on first use), or undefined if the browser will not keep one. */
export function getDeviceId(): string | undefined {
  try {
    const found = [Cookies.get(KEY), localStorage.getItem(KEY)].find((v) => v && UUID.test(v));
    const id = (found ?? newId()).toLowerCase();
    Cookies.set(KEY, id, { expires: FIVE_YEARS_IN_DAYS, sameSite: "lax", secure: location.protocol === "https:" });
    localStorage.setItem(KEY, id);
    return id;
  } catch {
    return undefined;
  }
}
